// /apps/patient/src/pages/billing/PaymentMethods.tsx

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { 
  Elements, 
  PaymentElement, 
  useStripe, 
  useElements 
} from '@stripe/react-stripe-js';
import { patientApiService } from '../../services/api';
import { PageHeader } from '../../components/layout/PageHeader';
import { PaymentMethod } from '@prism-ehr/shared';
import { 
  CreditCard, Plus, Trash2, Star, Loader2, 
  X, CheckCircle, AlertCircle 
} from 'lucide-react';

interface AddPaymentMethodFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

function AddPaymentMethodForm({ onSuccess, onCancel }: AddPaymentMethodFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    setIsProcessing(true);
    setErrorMessage('');

    try {
      // Confirm the setup
      const { error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/billing/payment-methods`,
        },
        redirect: 'if_required'
      });

      if (error) {
        setErrorMessage(error.message || 'Failed to save payment method');
      } else {
        // Success
        onSuccess();
      }
    } catch (error) {
      console.error('Error saving payment method:', error);
      setErrorMessage('An error occurred saving your payment method');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement 
        options={{
          layout: 'tabs',
          paymentMethodOrder: ['card']
        }}
      />

      {errorMessage && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start">
          <AlertCircle className="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-600">{errorMessage}</p>
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Payment Method'
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isProcessing}
          className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export function PaymentMethods() {
  const navigate = useNavigate();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [stripePromise, setStripePromise] = useState<any>(null);
  const [setupIntent, setSetupIntent] = useState<any>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    loadPaymentMethods();
    loadStripeConfig();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      const response = await patientApiService.getMyPaymentMethods();
      setPaymentMethods(response.payment_methods || []);
      setDefaultPaymentMethodId(response.default_payment_method_id || null);
    } catch (error) {
      console.error('Error loading payment methods:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStripeConfig = async () => {
    try {
      const config = await patientApiService.getStripeConfig();
      
      // Initialize Stripe
      const stripe = loadStripe(config.stripe_publishable_key, {
        stripeAccount: config.stripe_account_id
      });
      setStripePromise(stripe);
    } catch (error) {
      console.error('Failed to load Stripe configuration:', error);
    }
  };

  const handleAddNew = async () => {
    try {
      const response = await patientApiService.createSetupIntent();
      setSetupIntent(response);
      setShowAddForm(true);
    } catch (error) {
      console.error('Error creating setup intent:', error);
      alert('Failed to initialize payment form');
    }
  };

  const handleSetDefault = async (paymentMethodId: string) => {
    try {
      await patientApiService.setDefaultPaymentMethod(paymentMethodId);
      setDefaultPaymentMethodId(paymentMethodId);
      
      // Update local state
      setPaymentMethods(methods => 
        methods.map(m => ({
          ...m,
          is_default: m.stripe_payment_method_id === paymentMethodId
        }))
      );
    } catch (error) {
      console.error('Error setting default payment method:', error);
      alert('Failed to set default payment method');
    }
  };

  const handleDelete = async (paymentMethodId: string) => {
    if (!confirm('Are you sure you want to remove this payment method?')) return;

    setDeletingId(paymentMethodId);
    try {
      await patientApiService.deletePaymentMethod(paymentMethodId);
      setPaymentMethods(methods => 
        methods.filter(m => m.stripe_payment_method_id !== paymentMethodId)
      );
    } catch (error) {
      console.error('Error deleting payment method:', error);
      alert('Failed to remove payment method');
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddSuccess = () => {
    setShowAddForm(false);
    setSetupIntent(null);
    loadPaymentMethods();
  };

  const getCardIcon = (brand: string) => {
    // Could add specific brand icons here
    return <CreditCard className="h-8 w-8 text-gray-400" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader 
          title="Payment Methods" 
          showBack={true}
          actions={
            !showAddForm && (
              <button
                onClick={handleAddNew}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Payment Method
              </button>
            )
          }
        />

        {/* Add New Payment Method Form */}
        {showAddForm && stripePromise && setupIntent && (
          <div className="mt-6 bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-6">Add Payment Method</h3>
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret: setupIntent.client_secret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#0fadc7'
                  }
                }
              }}
            >
              <AddPaymentMethodForm
                onSuccess={handleAddSuccess}
                onCancel={() => {
                  setShowAddForm(false);
                  setSetupIntent(null);
                }}
              />
            </Elements>
          </div>
        )}

        {/* Payment Methods List */}
        {!showAddForm && (
          <div className="mt-6">
            {paymentMethods.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No payment methods</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Add a payment method to make payments faster and easier.
                </p>
                <div className="mt-6">
                  <button
                    onClick={handleAddNew}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Payment Method
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <ul className="divide-y divide-gray-200">
                  {paymentMethods.map((method) => (
                    <li key={method.id} className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          {getCardIcon(method.card_brand)}
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-900">
                              {method.card_brand} •••• {method.card_last_four}
                            </p>
                            <p className="text-sm text-gray-500">
                              Expires {method.card_exp_month}/{method.card_exp_year}
                            </p>
                            {method.is_default && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Default
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {!method.is_default && (
                            <button
                              onClick={() => handleSetDefault(method.stripe_payment_method_id)}
                              className="text-sm text-primary hover:text-primary-dark"
                            >
                              Set as default
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(method.stripe_payment_method_id)}
                            disabled={deletingId === method.stripe_payment_method_id}
                            className="text-gray-400 hover:text-red-500 disabled:opacity-50"
                          >
                            {deletingId === method.stripe_payment_method_id ? (
                              <Loader2 className="h-5 w-5 animate-spin" />
                            ) : (
                              <Trash2 className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Security Notice */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p className="flex items-center justify-center gap-1">
            <CheckCircle className="h-4 w-4" />
            Your payment information is stored securely by Stripe
          </p>
          <p className="mt-1">
            We never store your full card details on our servers
          </p>
        </div>
      </div>
    </div>
  );
}